// Phase 282e — Public Star Academy Social Proof Strip
// Sits on the landing page right after the headline stats band. Pulls the
// most-recent LOCKED quarterly podium and renders it as real social proof.
// Auto-hides silently when no quarter has been locked yet — never shows a
// placeholder "Q1 2026: nobody" awkward state on a fresh deploy.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Trophy, Medal, Award, MapPin } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const RANK_ICONS = { 1: Trophy, 2: Medal, 3: Award };
const RANK_RING = {
  1: 'ring-amber-400',
  2: 'ring-slate-400',
  3: 'ring-orange-500',
};
const RANK_LABEL = { 1: 'Gold', 2: 'Silver', 3: 'Bronze' };
const RANK_ICON_COLOR = {
  1: 'text-amber-500',
  2: 'text-slate-400',
  3: 'text-orange-500',
};

export default function StarAcademyProofStrip() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${BACKEND}/api/public/star-academies`, { timeout: 8000 })
      .then(res => {
        if (cancelled) return;
        setData(res.data);
      })
      .catch(() => {
        if (cancelled) return;
        setData(null);
      });
    return () => { cancelled = true; };
  }, []);

  // Silently hide while loading OR when no locked quarter exists yet
  if (!data || !data.podium || data.podium.length === 0) return null;

  const podium = data.podium.slice(0, 3);

  return (
    <section
      data-testid="landing-star-academies-strip"
      className="border-b border-border py-12 lg:py-16 bg-gradient-to-b from-amber-50/40 via-yellow-50/30 to-transparent dark:from-amber-950/10 dark:via-yellow-950/10 dark:to-transparent"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-300 text-[10px] font-bold tracking-wider uppercase">
            <Trophy className="w-3 h-3" /> Star Academies · {data.quarter_label}
          </span>
          <h2 className="text-2xl md:text-3xl font-display font-bold mt-3 text-slate-900 dark:text-slate-100">
            Trusted by India's top performing academies
          </h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl mx-auto">
            Recognised every quarter for the fastest portal activation velocity.
            Powered entirely by real customer data — refreshed automatically.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6 max-w-5xl mx-auto">
          {podium.map(p => {
            const Icon = RANK_ICONS[p.rank] || Trophy;
            return (
              <div
                key={p.rank}
                data-testid={`landing-star-rank-${p.rank}`}
                className={`relative rounded-2xl border bg-white/80 dark:bg-slate-900/40 backdrop-blur p-5 shadow-sm transition-transform duration-300 hover:-translate-y-1 hover:shadow-md ${
                  p.rank === 1
                    ? 'border-amber-200 dark:border-amber-800 md:scale-[1.04]'
                    : 'border-slate-200 dark:border-slate-700'
                }`}
              >
                <div className="absolute -top-3 -right-3">
                  <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full bg-white dark:bg-slate-800 ring-2 ring-offset-2 ring-offset-amber-50 dark:ring-offset-slate-900 ${RANK_RING[p.rank] || 'ring-slate-300'}`}>
                    <Icon className={`h-5 w-5 ${RANK_ICON_COLOR[p.rank] || 'text-slate-400'}`} />
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  {p.logo_url ? (
                    <img
                      src={p.logo_url}
                      alt={`${p.academy_name} logo`}
                      className="w-12 h-12 rounded-xl object-cover bg-slate-100 dark:bg-slate-800"
                      data-testid={`landing-star-logo-${p.rank}`}
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-800 text-slate-600 dark:text-slate-300 font-bold text-lg">
                      {(p.academy_name || '?').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase font-bold tracking-wider text-amber-700 dark:text-amber-400">
                      {RANK_LABEL[p.rank] || `#${p.rank}`}
                    </p>
                    <h3 className="text-base font-display font-semibold text-slate-900 dark:text-slate-100 truncate">
                      {p.academy_name}
                    </h3>
                    {p.city ? (
                      <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                        <MapPin className="w-3 h-3" /> {p.city}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-700 grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-[9px] uppercase font-semibold tracking-wider text-muted-foreground">
                      Activations
                    </p>
                    <p className="text-xl font-display font-bold tabular-nums text-slate-900 dark:text-slate-100">
                      {p.total_activations}
                    </p>
                  </div>
                  <div>
                    <p className="text-[9px] uppercase font-semibold tracking-wider text-muted-foreground">
                      Velocity
                    </p>
                    <p className="text-xl font-display font-bold tabular-nums text-emerald-600 dark:text-emerald-400">
                      {p.velocity_per_day}
                      <span className="text-[10px] font-normal text-muted-foreground ml-0.5">/day</span>
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-center text-[10px] text-muted-foreground mt-8 uppercase tracking-wider font-semibold">
          Rankings reset every 90 days · Locked: {data.quarter_start} → {data.quarter_end}
        </p>
      </div>
    </section>
  );
}
